import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type Details = { client?: { name?: string; redirect_uri?: string }; scope?: string; scopes?: string; redirect_uri?: string; redirect_url?: string; redirect_to?: string } | null;
type OAuthResult = { data: Details; error: { message: string } | null };
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
  approveAuthorization: (id: string) => Promise<OAuthResult>;
  denyAuthorization: (id: string) => Promise<OAuthResult>;
};
const oauth = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

const SCOPE_LABELS: Record<string, string> = {
  openid: "Confirm your identity",
  email: "Share your email address",
  profile: "Share your basic profile",
};

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<Details>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) return setError("This connection link is missing its authorization ID.");
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = "/login?next=" + encodeURIComponent(next);
        return;
      }
      setEmail(sess.session.user.email ?? "");
      try {
        const { data, error } = await oauth().getAuthorizationDetails(authorizationId);
        if (!active) return;
        if (error) return setError(error.message || "This connection request is invalid or has expired.");
        const immediate = data?.redirect_url ?? data?.redirect_to;
        if (immediate && !data?.client) { window.location.href = immediate; return; }
        setDetails(data);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "Could not load this connection request.");
      }
    })();
    return () => { active = false; };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const { data, error } = approve
        ? await oauth().approveAuthorization(authorizationId)
        : await oauth().denyAuthorization(authorizationId);
      if (error) throw new Error(error.message);
      const target = data?.redirect_url ?? data?.redirect_to;
      if (!target) throw new Error("No redirect was returned.");
      window.location.href = target;
    } catch (e) {
      setBusy(false);
      setError(`${approve ? "Approval" : "Cancellation"} failed: ${e instanceof Error ? e.message : "unknown error"}`);
    }
  }

  const clientName = details?.client?.name ?? "An app";
  const scopes: string[] = String(details?.scope ?? details?.scopes ?? "").split(/[\s,]+/).filter(Boolean);
  const redirectUri: string = details?.redirect_uri ?? details?.client?.redirect_uri ?? "";

  return (
    <main className="flex min-h-screen items-center justify-center bg-secondary p-6">
      <Card className="w-full max-w-md">
        {error ? (
          <CardHeader>
            <CardTitle>Connection request unavailable</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        ) : !details ? (
          <CardHeader><CardDescription>Loading connection request…</CardDescription></CardHeader>
        ) : (
          <>
            <CardHeader>
              <CardTitle>Connect {clientName} to Earth Connect</CardTitle>
              <CardDescription>
                This lets {clientName} use this app as you{email ? ` (${email})` : ""}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {redirectUri && <p className="text-muted-foreground">Returns to: {redirectUri}</p>}
              <ul className="list-disc pl-5 space-y-1">
                <li>Search marketplace listings</li>
                <li>Read your listings, lab reports and notifications</li>
                {scopes.map((s) => <li key={s}>{SCOPE_LABELS[s] ?? `Additional permission requested: ${s}`}</li>)}
              </ul>
              <p className="text-muted-foreground">This does not bypass this app's permissions or backend policies.</p>
              <div className="flex gap-2">
                <Button className="flex-1" disabled={busy} onClick={() => decide(true)}>Approve</Button>
                <Button className="flex-1" variant="outline" disabled={busy} onClick={() => decide(false)}>Cancel connection</Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </main>
  );
}
